import mermaid from "mermaid";

import { sanitizeMermaid } from "./MermaidDiagram.tsx";

let counter = 0;

/** Escape text for safe interpolation into the standalone HTML document. */
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Render a Mermaid diagram to SVG and open it in a new browser tab as a
 * standalone, self-contained HTML page and presentation slide for calls.
 */
export async function openDiagramInNewTab(title: string, mermaidSource: string): Promise<void> {
  const cleaned = sanitizeMermaid(mermaidSource);
  const id = `mermaid-newtab-${++counter}`;
  let svg = "";
  try {
    ({ svg } = await mermaid.render(id, cleaned));
  } catch {
    svg = `<pre style="white-space:pre-wrap">${esc(mermaidSource)}</pre>`;
  }

  const safeTitle = esc(title || "SellSynq Architecture Diagram");
  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${safeTitle} · SellSynq Architecture Slide</title>
<style>
  :root { color-scheme: light dark; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    background: #0b0f19;
    color: #f1f5f9;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }
  header {
    padding: 12px 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: #111827;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  body.fullscreen-present header {
    display: none;
  }
  body.fullscreen-present main {
    padding: 0;
    height: 100vh;
  }
  body.fullscreen-present .diagram-card {
    border: none;
    border-radius: 0;
    box-shadow: none;
    max-width: 100vw;
    max-height: 100vh;
    padding: 20px;
  }
  .title-group {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .logo-badge {
    background: linear-gradient(135deg, #4f46e5, #7c3aed);
    color: white;
    font-size: 11px;
    font-weight: 700;
    padding: 4px 8px;
    border-radius: 6px;
    letter-spacing: 0.04em;
  }
  h1 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #f8fafc;
  }
  .actions {
    display: flex;
    gap: 8px;
  }
  .btn {
    background: #1e293b;
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #f8fafc;
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .btn:hover {
    background: #334155;
    border-color: rgba(255, 255, 255, 0.25);
  }
  .btn-primary {
    background: #4f46e5;
    border-color: #6366f1;
    color: #fff;
  }
  .present-pill {
    background: rgba(16, 185, 129, 0.15);
    border: 1px solid rgba(16, 185, 129, 0.4);
    color: #34d399;
  }
  main {
    flex: 1;
    padding: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    background: radial-gradient(circle at center, #1e293b 0%, #0b0f19 100%);
  }
  .diagram-card {
    background: #ffffff;
    color: #1e293b;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
    max-width: 95vw;
    max-height: calc(100vh - 120px);
    overflow: auto;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .diagram-card svg {
    max-width: 100%;
    height: auto;
  }
  @media print {
    header { display: none; }
    body, main { background: #fff !important; }
    .diagram-card { border: none !important; box-shadow: none !important; padding: 0 !important; }
  }
</style>
</head>
<body>
<header>
  <div class="title-group">
    <span class="logo-badge">SELLSYNQ LIVE DIAGRAM</span>
    <h1>${safeTitle}</h1>
  </div>
  <div class="actions">
    <button onclick="toggleFullscreen()" class="btn present-pill" title="Present full screen to share on Google Meet / Zoom">
      🖥️ Present Fullscreen (Share Screen)
    </button>
    <button onclick="window.print()" class="btn btn-primary">🖨 Print / PDF</button>
  </div>
</header>
<main>
  <div class="diagram-card">
    ${svg}
  </div>
</main>

<script>
  function toggleFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        document.body.classList.add('fullscreen-present');
      }).catch(() => {});
    } else {
      document.exitFullscreen().then(() => {
        document.body.classList.remove('fullscreen-present');
      }).catch(() => {});
    }
  }

  document.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement) {
      document.body.classList.remove('fullscreen-present');
    } else {
      document.body.classList.add('fullscreen-present');
    }
  });
</script>
</body>
</html>`;

  const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
